import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, FolderOpen, Pencil, Plus, Trash2, Users } from 'lucide-react';
import { PersonaFormModal } from '@/components/modals/PersonaFormModal';
import { Button } from '@/components/ui/Button';
import {
  usePersonasStore,
  type CreatePersonaInput,
  type Persona,
  type UpdatePersonaInput,
} from '@/stores/personasStore';
import { useAuthStore } from '@/stores/authStore';

const PERSONA_LIMITS: Record<'free' | 'pro' | 'agency', number> = {
  free: 2,
  pro: 5,
  agency: -1,
};

export function PersonasPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const {
    personas,
    isLoading,
    error,
    fetchPersonas,
    createPersona,
    updatePersona,
    deletePersona,
    setDefaultPersona,
  } = usePersonasStore();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingPersona, setEditingPersona] = useState<Persona | null>(null);

  useEffect(() => {
    void fetchPersonas();
  }, [fetchPersonas]);

  const maxPersonas = useMemo(() => {
    if (!user) {
      return 2;
    }
    return PERSONA_LIMITS[user.plan] ?? 2;
  }, [user]);

  const hasReachedLimit = maxPersonas !== -1 && personas.length >= maxPersonas;

  const handleCreatePersona = async (data: CreatePersonaInput) => {
    await createPersona(data);
    setIsCreateModalOpen(false);
  };

  const handleEditPersona = async (data: CreatePersonaInput | UpdatePersonaInput) => {
    if (!editingPersona) {
      return;
    }
    await updatePersona(editingPersona.id, data);
    setEditingPersona(null);
  };

  const handleDeletePersona = async (personaId: string) => {
    await deletePersona(personaId);
  };

  const handleSetDefault = async (personaId: string) => {
    await setDefaultPersona(personaId);
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <section className="brutalist-panel relative overflow-hidden rounded-3xl p-6 lg:p-8">
        <div className="absolute right-6 top-6 hidden rounded-full border-2 border-border px-3 py-1 text-xs font-black uppercase tracking-[0.2em] text-muted-foreground lg:block">
          {maxPersonas === -1 ? 'Agency plan' : `${personas.length}/${maxPersonas} personas`}
        </div>
        <div className="max-w-4xl">
          <p className="brutalist-label">BeLoud Personas</p>
          <h1 className="mt-3 text-4xl font-black leading-none tracking-[-0.08em] md:text-6xl">
            Managed voices for professional publishing.
          </h1>
          <p className="mt-4 max-w-2xl text-base font-medium text-muted-foreground md:text-lg">
            Keep each client or audience workspace clear with voice, language, network context and generated-post activity.
          </p>
        </div>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button
            className="rounded-xl border-2 border-border bg-primary px-5 py-3 text-sm font-black text-primary-foreground shadow-[4px_4px_0_hsl(var(--foreground)/0.22)] transition-transform hover:-translate-y-0.5 dark:shadow-[4px_4px_0_hsl(var(--primary)/0.24)]"
            onClick={() => setIsCreateModalOpen(true)}
            disabled={hasReachedLimit || isLoading}
          >
            <Plus className="mr-2 h-4 w-4" />
            Nuova Persona
          </Button>
          <div className="flex items-center rounded-xl border-2 border-border bg-card px-4 py-2 text-sm font-black text-muted-foreground">
            {maxPersonas === -1 ? 'Unlimited workspaces' : `${Math.max(maxPersonas - personas.length, 0)} slots available`}
          </div>
        </div>
      </section>

      {error ? (
        <div className="brutalist-panel-soft rounded-2xl border-destructive/70 bg-destructive/5 p-4 text-sm font-bold text-destructive">
          {error}
        </div>
      ) : null}

      {hasReachedLimit ? (
        <div className="brutalist-panel-soft flex flex-col items-start justify-between gap-3 rounded-2xl bg-primary/5 p-4 md:flex-row md:items-center">
          <p className="text-sm font-bold">
            Vuoi piu Personas? Passa a Pro per gestire fino a 5 identita.
          </p>
          <Button variant="outline" onClick={() => (window.location.href = '/plans')}>
            Upgrade
          </Button>
        </div>
      ) : null}

      {isLoading && personas.length === 0 ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={`persona-skeleton-${index}`} className="brutalist-panel-soft rounded-3xl p-5">
              <div className="mb-5 flex items-center gap-3">
                <div className="h-12 w-12 animate-pulse rounded-xl border-2 border-border bg-muted" />
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="h-5 w-2/3 animate-pulse rounded bg-muted" />
                  <div className="h-4 w-1/2 animate-pulse rounded bg-muted" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-4 w-full animate-pulse rounded bg-muted" />
                <div className="h-4 w-5/6 animate-pulse rounded bg-muted" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {personas.map((persona) => {
            const avatarInitial = persona.name.charAt(0).toUpperCase();
            const targetsCount = persona.targets_count ?? 0;
            const postsCount = persona.posts_generated ?? 0;

            return (
              <article
                key={persona.id}
                className={`brutalist-panel-soft flex min-h-full flex-col rounded-3xl p-5 transition-transform hover:-translate-y-1 ${persona.is_default ? 'border-primary bg-primary/5' : ''}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 flex-1 items-center gap-3 text-left">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border-2 border-border bg-primary text-base font-black text-primary-foreground">
                      {avatarInitial}
                    </div>
                    <div className="min-w-0">
                      <h2 className="truncate text-xl font-black tracking-[-0.06em]">{persona.name}</h2>
                      <p className="truncate text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
                        {persona.language}
                      </p>
                    </div>
                  </div>

                  {!persona.is_default ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 rounded-xl border-2 border-border bg-card hover:bg-secondary"
                      aria-label={`Imposta ${persona.name} come persona default`}
                      onClick={() => void handleSetDefault(persona.id)}
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                  ) : (
                    <span className="status-acid rounded-full border border-border px-2 py-1 text-[11px] font-black uppercase tracking-wide">
                      Default
                    </span>
                  )}
                </div>

                <p className="mt-4 line-clamp-3 min-h-[3.75rem] text-sm font-medium text-muted-foreground">
                  {persona.style_notes || 'Nessuna descrizione disponibile.'}
                </p>

                <div className="mt-5 grid grid-cols-2 gap-2 text-sm">
                  <div className="rounded-2xl border-2 border-border bg-background/45 p-3">
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-muted-foreground">Network</p>
                    <p className="mt-1 text-2xl font-black tracking-[-0.06em]">{targetsCount}</p>
                  </div>
                  <div className="rounded-2xl border-2 border-border bg-background/45 p-3">
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-muted-foreground">Posts</p>
                    <p className="mt-1 text-2xl font-black tracking-[-0.06em]">{postsCount}</p>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2 text-xs font-black">
                  <span className="rounded-full border border-border bg-secondary px-2 py-1">{persona.tone}</span>
                  <span className="rounded-full border border-border bg-card px-2 py-1">{persona.language}</span>
                </div>

                <div className="mt-auto flex flex-wrap items-center justify-end gap-2 pt-5">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-xl border-2 border-border font-black"
                    onClick={() => navigate(`/personas/${persona.id}`)}
                  >
                    <FolderOpen className="mr-1 h-4 w-4" />
                    Workspace
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-xl border-2 border-border font-black"
                    onClick={() => setEditingPersona(persona)}
                    disabled={isLoading}
                  >
                    <Pencil className="mr-1 h-4 w-4" />
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-xl font-black text-destructive hover:bg-destructive/10"
                    onClick={() => void handleDeletePersona(persona.id)}
                    disabled={isLoading}
                  >
                    <Trash2 className="mr-1 h-4 w-4" />
                    Delete
                  </Button>
                </div>
              </article>
            );
          })}

          {personas.length === 0 ? (
            <div className="brutalist-panel-soft rounded-3xl border-dashed p-10 text-center md:col-span-2 xl:col-span-3">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border-2 border-border bg-secondary">
                <Users className="h-7 w-7 text-muted-foreground" />
              </div>
              <p className="mt-4 text-xl font-black tracking-[-0.06em]">Nessuna persona configurata</p>
              <p className="mx-auto mt-2 max-w-md text-sm font-medium text-muted-foreground">
                Crea la tua prima persona per personalizzare tono e stile dei contenuti.
              </p>
            </div>
          ) : null}
        </section>
      )}

      <PersonaFormModal
        isOpen={isCreateModalOpen}
        mode="create"
        isLoading={isLoading}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={handleCreatePersona}
      />

      <PersonaFormModal
        isOpen={Boolean(editingPersona)}
        mode="edit"
        persona={editingPersona ?? undefined}
        isLoading={isLoading}
        onClose={() => setEditingPersona(null)}
        onSave={handleEditPersona}
      />
    </div>
  );
}
